'use client';
// src/components/Layout/BottomTabBar.jsx
//
// Native-app-style bottom tab bar. Replaces the hamburger as the primary
// nav surface on mobile (the hamburger sticks around for "More" — less-
// common nav like settings, support, and team picker).
//
// Only renders on mobile (`md:hidden`). Desktop + tablet keep the full
// sidebar.
//
// Role-aware:
//   - Parent: Home / Schedule / Chat / More            (4 tabs)
//   - Coach:  Home / Teams / Schedule / Chat / More    (5 tabs)
//
// Capacitor / iOS: padding-bottom uses `env(safe-area-inset-bottom)` so
// the bar floats above the iOS home indicator. Each tap target is 56px+
// tall to meet Apple HIG accessibility minimums.

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useRole } from '@/context/RoleContext';
import useInboxUnreadCount from '@/hooks/useInboxUnreadCount';
import { useTeam } from '@/context/TeamContext';
import {
  TrophyIcon,
  UserGroupIcon,
  QueueListIcon,
  ChatBubbleBottomCenterTextIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  TrophyIcon as TrophySolid,
  UserGroupIcon as UserGroupSolid,
  QueueListIcon as QueueListSolid,
  ChatBubbleBottomCenterTextIcon as ChatSolid,
  Bars3Icon as Bars3Solid,
} from '@heroicons/react/24/solid';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/**
 * Bottom tabs are role-aware via hasCoachTeams (per-team membership)
 * rather than userProfile.role (global signup hint). A pure parent
 * with no coach teams gets the lean 3-tab set; anyone with at least
 * one coach team gets the full 4-tab coach surface.
 *
 * "Home" is /dashboard for everyone — the universal team picker.
 * The old PARENT_TABS pointed to /dashboard already; coach added
 * /teams (My Teams admin). Both still land on the team picker via
 * /dashboard, so the only difference is whether the My Teams admin
 * tab is exposed.
 */
// Labels resolved at render from the app language — the tab bar is the
// PRIMARY nav on mobile (the pilot's actual device) and was the last
// hardcoded-English chrome there (2026-07-24 sidebar audit).
const TAB_LABELS = {
  en: { home: 'Home', teams: 'Teams', schedule: 'Schedule', chat: 'Chat', more: 'More', moreAria: 'Open more navigation', unread: (n) => `${n} unread` },
  es: { home: 'Inicio', teams: 'Equipos', schedule: 'Calendario', chat: 'Chat', more: 'Más', moreAria: 'Abrir más navegación', unread: (n) => `${n} sin leer` },
};

const TABS_PARENT = [
  { to: '/dashboard',    labelKey: 'home',     icon: TrophyIcon, activeIcon: TrophySolid },
  { to: '/schedule',     labelKey: 'schedule', icon: QueueListIcon, activeIcon: QueueListSolid },
  { to: '/messages',     labelKey: 'chat',     icon: ChatBubbleBottomCenterTextIcon, activeIcon: ChatSolid, showUnread: true },
];

const TABS_COACH = [
  { to: '/dashboard',    labelKey: 'home',     icon: TrophyIcon, activeIcon: TrophySolid },
  { to: '/teams',        labelKey: 'teams',    icon: UserGroupIcon, activeIcon: UserGroupSolid, matchPrefix: '/teams' },
  { to: '/schedule',     labelKey: 'schedule', icon: QueueListIcon, activeIcon: QueueListSolid, matchPrefix: '/schedule' },
  { to: '/messages',     labelKey: 'chat',     icon: ChatBubbleBottomCenterTextIcon, activeIcon: ChatSolid, matchPrefix: '/messages', showUnread: true },
];

function TabItem({ tab, active, unreadCount, L }) {
  const Icon = active ? tab.activeIcon : tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={!tab.matchPrefix}
      className={clsx(
        // 56px+ tap target, native-feel press scaling, no horizontal scroll
        'relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-all',
        'active:scale-[0.92]',
        active ? 'text-[#b5f03d]' : 'text-white/55 hover:text-white/80',
      )}
      style={{ minHeight: 56 }}
      aria-current={active ? 'page' : undefined}
    >
      <span className="relative">
        <Icon className="h-6 w-6" />
        {tab.showUnread && unreadCount > 0 && (
          <span
            className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold leading-none text-[#0d1f14] bg-[#b5f03d] ring-1 ring-[#0d1f14]"
            style={{ fontVariantNumeric: 'tabular-nums' }}
            aria-label={L.unread(unreadCount)}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </span>
      <span
        className="text-[10px] font-bold uppercase tracking-[0.06em] leading-none"
        style={{ fontFamily: HEAD_FONT }}
      >
        {L[tab.labelKey]}
      </span>
      {/* Active indicator pill above the icon */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute top-0 left-1/2 -translate-x-1/2 h-0.5 w-8 rounded-b-full bg-[#b5f03d]"
        />
      )}
    </NavLink>
  );
}

function MoreTab({ onClick, active, L }) {
  const Icon = active ? Bars3Solid : Bars3Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-all',
        'active:scale-[0.92]',
        active ? 'text-[#b5f03d]' : 'text-white/55 hover:text-white/80',
      )}
      style={{ minHeight: 56 }}
      aria-label={L.moreAria}
    >
      <Icon className="h-6 w-6" />
      <span
        className="text-[10px] font-bold uppercase tracking-[0.06em] leading-none"
        style={{ fontFamily: HEAD_FONT }}
      >
        {L.more}
      </span>
    </button>
  );
}

export default function BottomTabBar({ onOpenMore, drawerOpen }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const L = TAB_LABELS[String(i18n?.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'];
  const { userProfile, firebaseUser } = useAuth();
  const { currentTeam } = useTeam();

  const { hasCoachTeams } = useRole();
  const tabs = hasCoachTeams ? TABS_COACH : TABS_PARENT;

  // Unread count for the chat tab badge — reuses the same hook the
  // sidebar uses, so the number stays in sync.
  const scope = currentTeam ? 'team' : 'user';
  const targetId = currentTeam ? currentTeam.id : (firebaseUser?.uid || null);
  const { count: chatUnreadCount } = useInboxUnreadCount(targetId, { scope });

  const isActive = (tab) => {
    if (tab.matchPrefix) return location.pathname.startsWith(tab.matchPrefix);
    return location.pathname === tab.to;
  };

  return (
    <nav
      role="navigation"
      aria-label="Primary"
      className={clsx(
        // Fixed bottom, full width, mobile-only. Pitch background w/ subtle
        // top border. Safe-area-inset for iOS home indicator.
        'fixed bottom-0 inset-x-0 z-40 md:hidden',
        'bg-[#0d1f14] border-t border-white/10 backdrop-blur-md',
        'flex',
      )}
      style={{
        paddingBottom: 'max(0.25rem, env(safe-area-inset-bottom))',
      }}
    >
      {tabs.map((tab) => (
        <TabItem
          key={tab.to}
          tab={tab}
          active={isActive(tab)}
          unreadCount={tab.showUnread ? chatUnreadCount : 0}
          L={L}
        />
      ))}
      <MoreTab onClick={onOpenMore} active={!!drawerOpen} L={L} />
    </nav>
  );
}
